import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs, doc, updateDoc, addDoc, query, orderBy, deleteDoc, getDoc } from 'firebase/firestore';
import { ref, onValue, remove, set, off } from 'firebase/database';
import { db, realtimeDb } from '../config/firebase';
import '../components/ChatWidget.css';
import ShinyText from '../components/ShinyText';
import { 
  Users, 
  Shield, 
  UserCheck, 
  UserX, 
  Crown, 
  RefreshCw,
  Search,
  Filter,
  Mail,
  Eye,
  CheckCircle,
  Clock,
  FileText,
  Check,
  X,
  Plus,
  Star,
  MessageCircle,
  Ban,
  Timer,
  Trash2,
  User,
  Send,
  Minus,
  Store,
  Edit,
  Save
} from 'lucide-react';
import SEO from '../components/SEO';

const AdminPanel: React.FC = () => {
  const { currentUser, isAdmin, getAllUsers, updateUserRole, toggleUserStatus } = useAuth();
  const { onlineUsers } = useChat();
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'user'>('all');
  const [updatingUsers, setUpdatingUsers] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'users' | 'messages' | 'applications' | 'server' | 'sponsors' | 'chat' | 'market'>('users');
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [isApplicationDetailOpen, setIsApplicationDetailOpen] = useState(false);
  const [serverSettings, setServerSettings] = useState<any>(null);
  const [editingServer, setEditingServer] = useState<any>(null);
  const [isServerEditModalOpen, setIsServerEditModalOpen] = useState(false);
  const [sponsors, setSponsors] = useState<any[]>([]);
  const [selectedSponsor, setSelectedSponsor] = useState<any>(null);
  const [isSponsorDetailOpen, setIsSponsorDetailOpen] = useState(false);
  
  // Chat moderasyon state'leri
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatSearchTerm, setChatSearchTerm] = useState('');
  const [chatFilter, setChatFilter] = useState<'all' | 'recent' | 'reported'>('all');
  const [adminMessage, setAdminMessage] = useState('');
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('adminChatFontSize');
    return saved ? parseInt(saved) : 14;
  });
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  // Ban ve timeout yönetimi state'leri
  const [bannedUsers, setBannedUsers] = useState<any[]>([]);
  const [timeoutUsers, setTimeoutUsers] = useState<any[]>([]);
  const [showBanTimeoutPanel, setShowBanTimeoutPanel] = useState(false);
  const [banTimeoutSearchTerm, setBanTimeoutSearchTerm] = useState('');
  
  // Market yönetimi state'leri
  const [marketProducts, setMarketProducts] = useState<any[]>([]);
  const [isProductEditModalOpen, setIsProductEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [marketSearchTerm, setMarketSearchTerm] = useState('');
  const [marketFilter, setMarketFilter] = useState<'all' | 'vehicles' | 'clothing' | 'weapons' | 'properties' | 'accessories' | 'games'>('all');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const usersData = await getAllUsers();
      
      // Online kullanıcıları kontrol et
      const usersWithOnlineStatus = usersData.map(user => ({
        ...user,
        isOnline: onlineUsers.some(onlineUser => onlineUser.userId === user.uid)
      }));
      
      setUsers(usersWithOnlineStatus);
      setError('');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [getAllUsers, onlineUsers]);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const messagesQuery = query(collection(db, 'messages'), orderBy('createdAt', 'desc'));
      const messagesSnapshot = await getDocs(messagesQuery);
      const messagesData = messagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setMessages(messagesData);
      setError('');
    } catch (err) {
      console.error('Mesajlar yüklenirken hata oluştu:', err);
      setError('Mesajlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  const markMessageAsRead = async (messageId: string) => {
    try {
      await updateDoc(doc(db, 'messages', messageId), {
        status: 'read',
        updatedAt: new Date()
      });
      
      // Local state'i güncelle
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, status: 'read' } : msg
      ));
    } catch (err) {
      console.error('Mesaj durumu güncellenirken hata oluştu:', err);
    }
  };

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      const applicationsQuery = query(collection(db, 'forms'), orderBy('createdAt', 'desc'));
      const applicationsSnapshot = await getDocs(applicationsQuery);
      const applicationsData = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setApplications(applicationsData);
      setError('');
    } catch (err) {
      console.error('Başvurular yüklenirken hata oluştu:', err);
      setError('Başvurular yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSponsors = useCallback(async () => {
    try {
      const sponsorsQuery = query(collection(db, 'sponsors'), orderBy('createdAt', 'desc'));
      const sponsorsSnapshot = await getDocs(sponsorsQuery);
      const sponsorsData = sponsorsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSponsors(sponsorsData);
    } catch (error) {
      console.error('Sponsorlar yüklenirken hata:', error);
    }
  }, []);

  const loadMarketProducts = useCallback(async () => {
    try {
      const productsQuery = query(collection(db, 'marketProducts'), orderBy('createdAt', 'desc'));
      const productsSnapshot = await getDocs(productsQuery);
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMarketProducts(productsData);
    } catch (error) {
      console.error('Market ürünleri yüklenirken hata:', error);
    }
  }, []);

  // Market ürünleri CRUD fonksiyonları
  const addMarketProduct = async (productData: any) => {
    try {
      const docRef = await addDoc(collection(db, 'marketProducts'), {
        ...productData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      await loadMarketProducts();
      return docRef.id;
    } catch (error) {
      console.error('Ürün eklenirken hata:', error);
      throw error;
    }
  };

  const updateMarketProduct = async (productId: string, productData: any) => {
    try {
      const productRef = doc(db, 'marketProducts', productId);
      await updateDoc(productRef, {
        ...productData,
        updatedAt: new Date()
      });
      await loadMarketProducts();
    } catch (error) {
      console.error('Ürün güncellenirken hata:', error);
      throw error;
    }
  };

  const deleteMarketProduct = async (productId: string) => {
    try {
      await deleteDoc(doc(db, 'marketProducts', productId));
      await loadMarketProducts();
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
      throw error;
    }
  };

  // Chat mesajlarını yükle
  const loadChatMessages = useCallback(async () => {
    try {
      const messagesRef = ref(realtimeDb, 'messages');
      onValue(messagesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const messagesList = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          })).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
          setChatMessages(messagesList);
        } else {
          setChatMessages([]);
        }
      });
    } catch (error) {
      console.error('Chat mesajları yüklenirken hata:', error);
    }
  }, []);

  // Chat moderasyon aksiyonları
  const deleteMessage = async (messageId: string) => {
    try {
      await remove(ref(realtimeDb, `messages/${messageId}`));
      // State güncellemesine gerek yok, listener otomatik güncelleyecek
    } catch (error) {
      console.error('Mesaj silinirken hata:', error);
    }
  };

  const banUser = async (userId: string, reason: string) => {
    try {
      // Firestore'da kullanıcıyı banla
      await updateDoc(doc(db, 'users', userId), {
        isBanned: true,
        banReason: reason,
        bannedAt: new Date(),
        bannedBy: currentUser?.uid
      });
      
      // Realtime Database'de ban bilgisini kaydet
      try {
        const banData = {
          reason,
          bannedAt: Date.now(),
          bannedBy: currentUser?.uid,
          bannedByEmail: currentUser?.email
        };
        
        await set(ref(realtimeDb, `bans/${userId}`), banData);
      } catch (realtimeError: any) {
        // Realtime Database hatası olsa bile Firestore işlemi başarılı oldu
        // Hata loglanmaz - production güvenliği için
      }
      
      // Banlanan kullanıcı listesini yenile
      await loadBannedUsers();
      
    } catch (error: any) {
      // Hata yönetimi - loglanmaz güvenlik nedeniyle
      setError('Kullanıcı banlanırken bir hata oluştu');
    }
  };

  const timeoutUser = async (userId: string, duration: number, reason: string) => {
    try {
      const timeoutUntil = Date.now() + (duration * 60 * 1000); // dakika cinsinden
      
      // Realtime Database'de timeout bilgisini kaydet
      try {
        await set(ref(realtimeDb, `timeouts/${userId}`), {
          reason,
          timeoutUntil,
          timedOutAt: Date.now(),
          timedOutBy: currentUser?.uid,
          timedOutByEmail: currentUser?.email
        });
      } catch (realtimeError: any) {
        // Realtime Database hatası olsa bile işlem devam edebilir
        // Hata loglanmaz - production güvenliği için
      }
      
      // Timeout kullanıcı listesini yenile
      await loadTimeoutUsers();
      
    } catch (error: any) {
      setError('Kullanıcı timeout edilirken bir hata oluştu');
    }
  };

  // Banlanan kullanıcıları yükle
  const loadBannedUsers = useCallback(async () => {
    try {
      // Firestore'dan banlanan kullanıcıları al
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      const bannedUsersList = usersSnapshot.docs
        .map(doc => {
          const userData = doc.data();
          const processedUser = {
            id: doc.id,
            ...userData,
            // Kullanıcı adı için farklı alanları kontrol et
            displayName: userData.displayName || userData.name || userData.username || userData.email?.split('@')[0] || 'Bilinmeyen Kullanıcı',
            email: userData.email || 'Email bulunamadı'
          };
          
          return processedUser;
        })
        .filter((user: any) => user.isBanned === true);
      
      setBannedUsers(bannedUsersList);
    } catch (error) {
      console.error('Banlanan kullanıcılar yüklenirken hata:', error);
    }
  }, []);

  // Timeout edilen kullanıcıları yükle
  const loadTimeoutUsers = useCallback(async () => {
    try {
      // Realtime Database'den timeout bilgilerini al
      const timeoutRef = ref(realtimeDb, 'timeouts');
      onValue(timeoutRef, async (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const timeoutUsersList = await Promise.all(
            Object.keys(data).map(async (userId) => {
              const timeoutData = data[userId];
              const now = Date.now();
              const timeoutUntil = timeoutData.timeoutUntil;
              
              // Sadece aktif timeout'ları göster
              if (timeoutUntil && now < timeoutUntil) {
                // Firestore'dan kullanıcı bilgilerini al
                try {
                  const userDoc = await getDoc(doc(db, 'users', userId));
                  const userData = userDoc.exists() ? userDoc.data() : {};
                  
                  const processedUser = {
                    id: userId,
                    ...timeoutData,
                    // Kullanıcı adı için farklı alanları kontrol et
                    displayName: userData.displayName || userData.name || userData.username || timeoutData.displayName || timeoutData.username || userData.email?.split('@')[0] || 'Bilinmeyen Kullanıcı',
                    email: userData.email || timeoutData.email || 'Email bulunamadı',
                    remainingTime: Math.ceil((timeoutUntil - now) / 60000) // dakika cinsinden
                  };
                  
                  
                  return processedUser;
                } catch (error) {
                  console.error('Kullanıcı bilgileri alınırken hata:', error);
                  return {
                    id: userId,
                    ...timeoutData,
                    displayName: timeoutData.displayName || timeoutData.username || 'Bilinmeyen Kullanıcı',
                    email: timeoutData.email || 'Email bulunamadı',
                    remainingTime: Math.ceil((timeoutUntil - now) / 60000)
                  };
                }
              }
              return null;
            })
          );
          
          setTimeoutUsers(timeoutUsersList.filter(user => user !== null));
        } else {
          setTimeoutUsers([]);
        }
      });
    } catch (error) {
      console.error('Timeout kullanıcıları yüklenirken hata:', error);
    }
  }, []);

  // Kullanıcının banını aç
  const unbanUser = async (userId: string) => {
    try {
      // Firestore'da kullanıcının banını kaldır
      await updateDoc(doc(db, 'users', userId), {
        isBanned: false,
        banReason: null,
        bannedAt: null,
        bannedBy: null,
        unbannedAt: new Date(),
        unbannedBy: currentUser?.uid
      });
      
      // Realtime Database'den ban bilgisini sil
      try {
        await remove(ref(realtimeDb, `bans/${userId}`));
      } catch (realtimeError: any) {
        // Realtime Database hatası olsa bile Firestore işlemi başarılı oldu
        // Hata loglanmaz - production güvenliği için
      }
      
      // Banlanan kullanıcı listesini yenile
      await loadBannedUsers();
    } catch (error: any) {
      setError('Kullanıcının banı açılırken bir hata oluştu');
    }
  };

  // Kullanıcının timeout'unu kaldır
  const removeTimeout = async (userId: string) => {
    try {
      // Realtime Database'den timeout bilgisini sil
      try {
        await remove(ref(realtimeDb, `timeouts/${userId}`));
      } catch (realtimeError: any) {
        // Realtime Database hatası olsa bile işlem devam edebilir
        // Hata loglanmaz - production güvenliği için
      }
      
      // Timeout kullanıcı listesini yenile
      await loadTimeoutUsers();
    } catch (error: any) {
      setError('Kullanıcının timeout\'u kaldırılırken bir hata oluştu');
    }
  };

  // Admin mesaj gönder
  const sendAdminMessage = async () => {
    if (!adminMessage.trim() || !currentUser) return;

    try {
      const messageData = {
        id: Date.now().toString(),
        text: adminMessage.trim(),
        userId: currentUser.uid,
        username: currentUser.displayName || 'Admin',
        timestamp: Date.now(),
        isAdmin: true
      };

      // Realtime Database'e mesajı ekle
      await set(ref(realtimeDb, `messages/${messageData.id}`), messageData);
      
      // Input'u temizle
      setAdminMessage('');
      
    } catch (error: any) {
      setError('Mesaj gönderilirken bir hata oluştu');
    }
  };

  // Font boyutu değiştir
  const changeFontSize = (newSize: number) => {
    setFontSize(newSize);
    localStorage.setItem('adminChatFontSize', newSize.toString());
  };

  // Chat container'ı en alta scroll et
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Mesajlar değiştiğinde en alta scroll et
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Chat listener cleanup
  useEffect(() => {
    const messagesRef = ref(realtimeDb, 'messages');
    
    return () => {
      off(messagesRef);
    };
  }, []);

  const updateApplicationStatus = async (applicationId: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {
      await updateDoc(doc(db, 'forms', applicationId), {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Local state'i güncelle
      setApplications(prev => prev.map(app => 
        app.id === applicationId ? { ...app, status: newStatus } : app
      ));
    } catch (err) {
      console.error('Başvuru durumu güncellenirken hata oluştu:', err);
    }
  };

  const updateSponsorStatus = async (sponsorId: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {
      await updateDoc(doc(db, 'sponsors', sponsorId), {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Local state'i güncelle
      setSponsors(prev => prev.map(sponsor => 
        sponsor.id === sponsorId ? { ...sponsor, status: newStatus } : sponsor
      ));
    } catch (err) {
      console.error('Sponsor durumu güncellenirken hata oluştu:', err);
    }
  };

  const handleViewApplicationDetail = (application: any) => {
    setSelectedApplication(application);
    setIsApplicationDetailOpen(true);
  };

  const handleViewSponsorDetail = (sponsor: any) => {
    setSelectedSponsor(sponsor);
    setIsSponsorDetailOpen(true);
  };

  const loadServerSettings = useCallback(async () => {
    try {
      // Servers koleksiyonundan verileri yükle
      const serversRef = collection(db, 'servers');
      const serversSnapshot = await getDocs(serversRef);
      
      const serversList: any[] = [];
      serversSnapshot.forEach((doc) => {
        serversList.push({ id: doc.id, ...doc.data() });
      });
      
      // Server ayarlarını yükle
      const settingsQuery = query(collection(db, 'serverSettings'));
      const settingsSnapshot = await getDocs(settingsQuery);
      
      let settingsData = {
        id: 'main',
        discordLink: 'https://discord.gg/aedonv',
        applicationStatusText: 'Formunuz Kontrol Aşamasındadır',
        serverName: 'Aedon Community',
        serverDescription: 'En iyi FiveM sunucu deneyimi için buradayız!',
        servers: serversList
      };
      
      if (!settingsSnapshot.empty) {
        const existingSettings = settingsSnapshot.docs[0].data();
        settingsData = { 
          id: settingsSnapshot.docs[0].id,
          discordLink: existingSettings.discordLink || 'https://discord.gg/aedonv',
          applicationStatusText: existingSettings.applicationStatusText || 'Formunuz Kontrol Aşamasındadır',
          serverName: existingSettings.serverName || 'Aedon Community',
          serverDescription: existingSettings.serverDescription || 'En iyi FiveM sunucu deneyimi için buradayız!',
          servers: serversList 
        };
      }
      
      setServerSettings(settingsData);
    } catch (err) {
      console.error('Sunucu ayarları yüklenirken hata oluştu:', err);
    }
  }, []);

  const updateServerSettings = async (newSettings: any) => {
    try {
      if (serverSettings?.id === 'default' || !serverSettings?.id) {
        // Yeni doküman oluştur
        await addDoc(collection(db, 'serverSettings'), {
          ...newSettings,
          updatedAt: new Date()
        });
      } else {
        // Mevcut dokümanı güncelle
        await updateDoc(doc(db, 'serverSettings', serverSettings.id), {
          ...newSettings,
          updatedAt: new Date()
        });
      }
      
      setServerSettings((prev: any) => prev ? ({
        ...prev,
        ...newSettings
      }) : newSettings);
    } catch (err) {
      console.error('Sunucu ayarları güncellenirken hata oluştu:', err);
    }
  };

  // Server ekleme fonksiyonu
  const addServer = async (serverData: any) => {
    try {
      const docRef = await addDoc(collection(db, 'servers'), {
        ...serverData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      // Local state'i güncelle
      setServerSettings((prev: any) => ({
        ...prev,
        servers: [...(prev?.servers || []), { id: docRef.id, ...serverData }]
      }));
      
      return docRef.id;
    } catch (error) {
      console.error('Sunucu eklenirken hata:', error);
      throw error;
    }
  };

  // Server silme fonksiyonu
  const deleteServer = async (serverId: string) => {
    try {
      await deleteDoc(doc(db, 'servers', serverId));
      
      // Local state'i güncelle
      setServerSettings((prev: any) => ({
        ...prev,
        servers: prev?.servers?.filter((server: any) => server.id !== serverId) || []
      }));
    } catch (error) {
      console.error('Sunucu silinirken hata:', error);
      throw error;
    }
  };

  // Server güncelleme fonksiyonu
  const updateServer = async (serverId: string, serverData: any) => {
    try {
      await updateDoc(doc(db, 'servers', serverId), {
        ...serverData,
        updatedAt: new Date()
      });
      
      // Local state'i güncelle
      setServerSettings((prev: any) => ({
        ...prev,
        servers: prev?.servers?.map((server: any) => 
          server.id === serverId ? { ...server, ...serverData } : server
        ) || []
      }));
    } catch (error) {
      console.error('Sunucu güncellenirken hata:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (!isAdmin) {
      navigate('/');
      return;
    }

    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'messages') {
      loadMessages();
    } else if (activeTab === 'applications') {
      loadApplications();
    } else if (activeTab === 'sponsors') {
      loadSponsors();
    } else if (activeTab === 'market') {
      loadMarketProducts();
    } else if (activeTab === 'chat') {
      loadChatMessages();
      loadBannedUsers();
      loadTimeoutUsers();
    }
    
    // Server ayarlarını yükle
    loadServerSettings();
  }, [currentUser, isAdmin, navigate, loadUsers, loadMessages, loadApplications, loadSponsors, loadMarketProducts, loadChatMessages, loadBannedUsers, loadTimeoutUsers, activeTab, loadServerSettings]);

  const handleRoleChange = async (uid: string, newRole: 'admin' | 'user') => {
    try {
      await updateUserRole(uid, newRole);
      await loadUsers(); // Refresh the list
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleStatusToggle = async (uid: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      
      // Loading state'i başlat
      setUpdatingUsers(prev => new Set(prev).add(uid));
      
      await toggleUserStatus(uid, newStatus);
      
      // Optimistic update - UI'yi hemen güncelle
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.uid === uid 
            ? { ...user, isActive: newStatus }
            : user
        )
      );
      
      // Sonra veritabanından güncel veriyi çek
      await loadUsers();
      
    } catch (error: any) {
      setError(error.message || 'Durum güncellenirken bir hata oluştu');
    } finally {
      // Loading state'i bitir
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(uid);
        return newSet;
      });
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'Bilinmiyor';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Kullanıcıları online/offline durumlarına göre kategorize et
  const usersWithStatus = users.map(user => {
    const isOnline = onlineUsers.some(onlineUser => onlineUser.userId === user.uid);
    return {
      ...user,
      isOnline
    };
  });

  const filteredUsers = usersWithStatus.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // İstatistikler
  const totalUsers = users.length;
  const activeUsers = users.filter(user => user.isActive).length;
  const adminUsers = users.filter(user => user.role === 'admin').length;
  const onlineUsersCount = onlineUsers.length;
  const offlineUsersCount = totalUsers - onlineUsersCount;


  if (!currentUser || !isAdmin) {
    return null;
  }

  return (
    <>
      <SEO 
        title="Admin Panel - Aedon Community" 
        description="Aedon Community admin paneli - Kullanıcı yönetimi"
        keywords="admin, panel, kullanıcı yönetimi, aedon community"
      />
      
      <div className="min-h-screen bg-dark-900 pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-gaming font-bold text-white mb-2">
                  Admin Panel
                </h1>
                <p className="text-gray-400 text-sm sm:text-base">
                  Kullanıcı yönetimi ve sistem kontrolü
                </p>
              </div>
              
              <button
                onClick={loadUsers}
                disabled={loading}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors duration-300"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Yenile</span>
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="flex space-x-1 bg-gray-800/50 p-1 rounded-lg w-fit">
              <button
                onClick={() => setActiveTab('users')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'users'
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Kullanıcılar</span>
              </button>
              <button
                onClick={() => setActiveTab('messages')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'messages'
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <Mail className="w-4 h-4" />
                <span>Mesajlar</span>
                {messages.filter(msg => msg.status === 'unread').length > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {messages.filter(msg => msg.status === 'unread').length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('applications')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'applications'
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Başvurular</span>
                {applications.filter(app => app.status === 'pending').length > 0 && (
                  <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                    {applications.filter(app => app.status === 'pending').length}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setActiveTab('sponsors')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'sponsors'
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <Star className="w-4 h-4" />
                <span>Sponsorlar</span>
                {sponsors.filter(sponsor => sponsor.status === 'pending').length > 0 && (
                  <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                    {sponsors.filter(sponsor => sponsor.status === 'pending').length}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setActiveTab('chat')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'chat'
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <MessageCircle className="w-4 h-4" />
                <span>Chat Yönetimi</span>
                {chatMessages.length > 0 && (
                  <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                    {chatMessages.length}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setActiveTab('market')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'market'
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <Store className="w-4 h-4" />
                <span>Market Yönetimi</span>
                {marketProducts.length > 0 && (
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                    {marketProducts.length}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setActiveTab('server')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'server'
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <Shield className="w-4 h-4" />
                <span>Sunucu Ayarları</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-8">
            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Toplam Kullanıcı</p>
                  <p className="text-2xl font-bold text-white">{totalUsers}</p>
                </div>
                <Users className="w-8 h-8 text-primary-500" />
              </div>
            </div>

            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Çevrimiçi</p>
                  <p className="text-2xl font-bold text-green-400">{onlineUsersCount}</p>
                </div>
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              </div>
            </div>

            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Çevrimdışı</p>
                  <p className="text-2xl font-bold text-gray-400">{offlineUsersCount}</p>
                </div>
                <div className="w-3 h-3 bg-gray-500 rounded-full" />
              </div>
            </div>

            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Admin Sayısı</p>
                  <p className="text-2xl font-bold text-yellow-400">{adminUsers}</p>
                </div>
                <Crown className="w-8 h-8 text-yellow-500" />
              </div>
            </div>

            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Aktif Kullanıcı</p>
                  <p className="text-2xl font-bold text-white">{activeUsers}</p>
                </div>
                <UserCheck className="w-8 h-8 text-green-500" />
              </div>
            </div>
          </div>

          {/* Search and Filter */}
          {activeTab === 'users' && (
            <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl p-4 sm:p-6 mb-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Kullanıcı adı veya e-posta ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value as 'all' | 'admin' | 'user')}
                    className="px-3 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">Tüm Roller</option>
                    <option value="admin">Admin</option>
                    <option value="user">Kullanıcı</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
              {error}
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'users' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Kullanıcılar yükleniyor...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-dark-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Kullanıcı
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        E-posta
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Rol
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Online
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Kayıt Tarihi
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-700/50">
                    {filteredUsers.map((user) => (
                      <tr key={user.uid} className="hover:bg-dark-700/30 transition-colors duration-200">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary-400">
                                  {user.username.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-white">
                                {user.displayName || user.username}
                              </div>
                              <div className="text-sm text-gray-400">
                                @{user.username}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-300">{user.email}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.role === 'admin' 
                              ? 'bg-yellow-500/20 text-yellow-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {user.role === 'admin' ? (
                              <>
                                <Crown className="w-3 h-3 mr-1" />
                                Admin
                              </>
                            ) : (
                              <>
                                <Shield className="w-3 h-3 mr-1" />
                                Kullanıcı
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleStatusToggle(user.uid, user.isActive)}
                            disabled={updatingUsers.has(user.uid)}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-all duration-200 hover:scale-105 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                              user.isActive 
                                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                                : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                            }`}
                            title={`${user.isActive ? 'Pasifleştirmek için tıklayın' : 'Aktifleştirmek için tıklayın'}`}
                          >
                            {updatingUsers.has(user.uid) ? (
                              <>
                                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                Güncelleniyor...
                              </>
                            ) : user.isActive ? (
                              <>
                                <UserCheck className="w-3 h-3 mr-1" />
                                Aktif
                              </>
                            ) : (
                              <>
                                <UserX className="w-3 h-3 mr-1" />
                                Pasif
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-2 ${user.isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                            <span className={`text-sm ${user.isOnline ? 'text-green-400' : 'text-gray-400'}`}>
                              {user.isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <select
                              value={user.role}
                              onChange={(e) => handleRoleChange(user.uid, e.target.value as 'admin' | 'user')}
                              className="px-2 py-1 bg-dark-700/50 border border-dark-600/50 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-primary-500"
                            >
                              <option value="user">Kullanıcı</option>
                              <option value="admin">Admin</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </motion.div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-gray-400">Mesajlar yükleniyor...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Durum
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Gönderen
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          E-posta
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Konu
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Tarih
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-dark-700/50">
                      {messages.map((message) => (
                        <tr key={message.id} className="hover:bg-dark-700/30 transition-colors duration-200">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {message.status === 'unread' ? (
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                  <span className="text-xs text-red-400 font-medium">Okunmamış</span>
                                </div>
                              ) : message.status === 'read' ? (
                                <div className="flex items-center space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  <span className="text-xs text-green-400 font-medium">Okundu</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <Clock className="w-4 h-4 text-blue-500" />
                                  <span className="text-xs text-blue-400 font-medium">Yanıtlandı</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-white">{message.name}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">{message.email}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-white max-w-xs truncate">{message.subject}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">
                              {message.createdAt ? new Date(message.createdAt.seconds * 1000).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => markMessageAsRead(message.id)}
                                disabled={message.status === 'read'}
                                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
                              >
                                <Eye className="w-3 h-3" />
                                <span>Okundu İşaretle</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {messages.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                            Henüz mesaj bulunmuyor
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-gray-400">Başvurular yükleniyor...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Durum
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Kullanıcı
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Karakter Adı
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Tarih
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-dark-700/50">
                      {applications.map((application) => (
                        <tr key={application.id} className="hover:bg-dark-700/30 transition-colors duration-200">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {application.status === 'pending' ? (
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                  <span className="text-xs text-yellow-400 font-medium">Beklemede</span>
                                </div>
                              ) : application.status === 'approved' ? (
                                <div className="flex items-center space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  <span className="text-xs text-green-400 font-medium">Onaylandı</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <X className="w-4 h-4 text-red-500" />
                                  <span className="text-xs text-red-400 font-medium">Reddedildi</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-white">{application.userEmail}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">{application.characterName}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">
                              {application.createdAt ? new Date(application.createdAt.seconds * 1000).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {application.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => updateApplicationStatus(application.id, 'approved')}
                                    className="flex items-center space-x-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                                  >
                                    <Check className="w-3 h-3" />
                                    <span>Onayla</span>
                                  </button>
                                  <button
                                    onClick={() => updateApplicationStatus(application.id, 'rejected')}
                                    className="flex items-center space-x-1 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                    <span>Reddet</span>
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleViewApplicationDetail(application)}
                                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                              >
                                <Eye className="w-3 h-3" />
                                <span>Detay</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {applications.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                            Henüz başvuru bulunmuyor
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}

          {/* Sponsors Tab */}
          {activeTab === 'sponsors' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-gray-400">Sponsorlar yükleniyor...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Durum
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Şirket
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Sponsorluk Türü
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Bütçe
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Tarih
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-dark-700/50">
                      {sponsors.map((sponsor) => (
                        <tr key={sponsor.id} className="hover:bg-dark-700/30 transition-colors duration-200">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {sponsor.status === 'pending' ? (
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                  <span className="text-xs text-yellow-400 font-medium">Beklemede</span>
                                </div>
                              ) : sponsor.status === 'approved' ? (
                                <div className="flex items-center space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  <span className="text-xs text-green-400 font-medium">Onaylandı</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <X className="w-4 h-4 text-red-500" />
                                  <span className="text-xs text-red-400 font-medium">Reddedildi</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-white">{sponsor.companyName}</div>
                              <div className="text-sm text-gray-400">{sponsor.contactPerson}</div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-300">{sponsor.sponsorshipType}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-300">{sponsor.budget}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-400">
                              {sponsor.createdAt?.toDate ? 
                                sponsor.createdAt.toDate().toLocaleDateString('tr-TR') : 
                                'Bilinmiyor'
                              }
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewSponsorDetail(sponsor)}
                                className="text-primary-400 hover:text-primary-300 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {sponsor.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => updateSponsorStatus(sponsor.id, 'approved')}
                                    className="text-green-400 hover:text-green-300 transition-colors"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => updateSponsorStatus(sponsor.id, 'rejected')}
                                    className="text-red-400 hover:text-red-300 transition-colors"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {sponsors.length === 0 && (
                    <div className="text-center py-12">
                      <Star className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-400 mb-2">Henüz sponsor başvurusu yok</h3>
                      <p className="text-gray-500">Sponsor başvuruları burada görünecek</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Market Management Tab */}
          {activeTab === 'market' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white">Market Yönetimi</h3>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => {
                        setEditingProduct({
                          name: '',
                          category: 'vehicles',
                          description: '',
                          price: 0,
                          currency: 'TL',
                          image: '',
                          features: [],
                          author: currentUser?.displayName || 'Admin',
                          version: '1.0.0',
                          rating: 5.0,
                          downloads: 0,
                          players: 0,
                          server: ''
                        });
                        setIsProductEditModalOpen(true);
                      }}
                      className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Yeni Ürün Ekle</span>
                    </button>
                  </div>
                </div>

                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Ürün ara..."
                        value={marketSearchTerm}
                        onChange={(e) => setMarketSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={marketFilter}
                      onChange={(e) => setMarketFilter(e.target.value as any)}
                      className="px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">Tüm Kategoriler</option>
                      <option value="vehicles">Araçlar</option>
                      <option value="clothing">Kıyafetler</option>
                      <option value="weapons">Silahlar</option>
                      <option value="properties">Mülkler</option>
                      <option value="accessories">Aksesuarlar</option>
                      <option value="games">Oyunlar</option>
                    </select>
                  </div>
                </div>

                {/* Products List */}
                <div className="space-y-4">
                  {marketProducts
                    .filter(product => {
                      const matchesSearch = product.name.toLowerCase().includes(marketSearchTerm.toLowerCase()) ||
                                           product.description.toLowerCase().includes(marketSearchTerm.toLowerCase());
                      const matchesFilter = marketFilter === 'all' || product.category === marketFilter;
                      return matchesSearch && matchesFilter;
                    })
                    .map((product) => (
                    <div key={product.id} className="bg-dark-700/50 border border-dark-600 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div>
                            <h4 className="text-lg font-semibold text-white">{product.name}</h4>
                            <p className="text-gray-400 text-sm">{product.description}</p>
                            <div className="flex items-center space-x-4 mt-2">
                              <span className="text-green-400 font-bold">{product.price.toLocaleString()} {product.currency}</span>
                              <span className="text-gray-400 text-sm">Kategori: {product.category}</span>
                              <span className="text-gray-400 text-sm">Geliştirici: {product.author}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setEditingProduct(product);
                              setIsProductEditModalOpen(true);
                            }}
                            className="p-2 text-blue-400 hover:text-blue-300 hover:bg-dark-600 rounded-lg transition-colors"
                            title="Düzenle"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteMarketProduct(product.id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-dark-600 rounded-lg transition-colors"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {marketProducts.length === 0 && (
                    <div className="text-center py-12">
                      <Store className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-400 mb-2">
                        Henüz ürün bulunmuyor
                      </h3>
                      <p className="text-gray-500">
                        İlk ürünü eklemek için "Yeni Ürün Ekle" butonuna tıklayın
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Chat Management Tab */}
          {activeTab === 'chat' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white">Chat Yönetimi</h3>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setShowBanTimeoutPanel(true)}
                      className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 text-gray-300"
                      title="Ban ve Timeout Yönetimi"
                    >
                      <Shield className="w-4 h-4" />
                      {(bannedUsers.length > 0 || timeoutUsers.length > 0) && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {bannedUsers.length + timeoutUsers.length}
                        </span>
                      )}
                    </button>
                    
                    {/* Font Boyutu Ayarlama */}
                    <div className="flex items-center space-x-2 bg-gray-700/50 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-300">A-</span>
                      <button
                        onClick={() => changeFontSize(Math.max(10, fontSize - 2))}
                        className="text-gray-400 hover:text-white transition-colors"
                        title="Yazı boyutunu küçült"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-white min-w-[2rem] text-center">{fontSize}px</span>
                      <button
                        onClick={() => changeFontSize(Math.min(24, fontSize + 2))}
                        className="text-gray-400 hover:text-white transition-colors"
                        title="Yazı boyutunu büyüt"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-gray-300">A+</span>
                    </div>
                    
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Mesaj ara..."
                        value={chatSearchTerm}
                        onChange={(e) => setChatSearchTerm(e.target.value)}
                        className="w-64 px-4 py-2 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                    </div>
                    <select
                      value={chatFilter}
                      onChange={(e) => setChatFilter(e.target.value as 'all' | 'recent' | 'reported')}
                      className="px-4 py-2 bg-dark-700/50 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">Tüm Mesajlar</option>
                      <option value="recent">Son Mesajlar</option>
                      <option value="reported">Raporlanan</option>
                    </select>
                  </div>
                </div>

                <div 
                  ref={chatContainerRef}
                  className="space-y-4 max-h-96 overflow-y-auto"
                  style={{ fontSize: `${fontSize}px` }}
                >
                  {chatMessages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-400 mb-2">Henüz mesaj yok</h3>
                      <p className="text-gray-500">Chat mesajları burada görünecek</p>
                    </div>
                  ) : (
                    chatMessages
                      .filter(message => {
                        const matchesSearch = message.text?.toLowerCase().includes(chatSearchTerm.toLowerCase()) ||
                                            message.username?.toLowerCase().includes(chatSearchTerm.toLowerCase());
                        return matchesSearch;
                      })
                      .map((message) => {
                        const isAdminMessage = message.isAdmin || message.userId === currentUser?.uid;
                        return (
                          <div key={message.id} className={`${isAdminMessage ? 'ml-8' : 'mr-8'}`}>
                            <div className={`bg-dark-700/30 rounded-lg p-4 border border-dark-600/50 ${isAdminMessage ? 'bg-primary-600/20 border-primary-500/30' : ''}`}>
                              <div className={`flex ${isAdminMessage ? 'justify-end' : 'justify-between'} items-start mb-3`}>
                                {!isAdminMessage && (
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                                      <User className="w-4 h-4 text-white" />
                                    </div>
                                    <div>
                                      <h4 className="text-white font-medium flex items-center space-x-2">
                                        {message.isAdmin ? (
                                          <ShinyText 
                                            text={message.username || 'Admin'} 
                                            speed={7} 
                                            variant="black-white"
                                            className="font-bold text-sm"
                                          />
                                        ) : (
                                          <ShinyText 
                                            text={message.username || 'Bilinmeyen Kullanıcı'} 
                                            speed={5} 
                                            className="font-medium text-sm"
                                          />
                                        )}
                                        {message.isAdmin && <Shield className="w-4 h-4 text-yellow-400" />}
                                      </h4>
                                      <p className="text-xs text-gray-400">
                                        {message.timestamp ? new Date(message.timestamp).toLocaleString('tr-TR') : 'Bilinmeyen Tarih'}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                
                                {isAdminMessage && (
                                  <div className="flex items-center space-x-3">
                                    <div>
                                      <h4 className="text-white font-medium flex items-center space-x-2">
                                        <ShinyText 
                                          text={message.username || 'Admin'} 
                                          speed={7} 
                                          variant="black-white"
                                          className="font-bold text-sm"
                                        />
                                        <Shield className="w-4 h-4 text-yellow-400" />
                                      </h4>
                                      <p className="text-xs text-gray-400">
                                        {message.timestamp ? new Date(message.timestamp).toLocaleString('tr-TR') : 'Bilinmeyen Tarih'}
                                      </p>
                                    </div>
                                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                                      <Shield className="w-4 h-4 text-white" />
                                    </div>
                                  </div>
                                )}
                                
                                {!isAdminMessage && (
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => deleteMessage(message.id)}
                                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                                      title="Mesajı Sil"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => banUser(message.userId, 'Chat moderasyonu')}
                                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                                      title="Kullanıcıyı Banla"
                                    >
                                      <Ban className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => timeoutUser(message.userId, 10, 'Chat moderasyonu')}
                                      className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-colors"
                                      title="10 Dakika Timeout"
                                    >
                                      <Timer className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className={`bg-dark-600/30 rounded-lg p-3 ${isAdminMessage ? 'bg-primary-500/20' : ''}`}>
                                <p className="text-gray-300">{message.text || 'Mesaj içeriği yok'}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
                
                {/* Admin Mesaj Gönderme Alanı */}
                <div className="mt-6 border-t border-dark-600 pt-6">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                    <MessageCircle className="w-5 h-5 text-primary-400" />
                    <span>Admin Mesajı Gönder</span>
                  </h4>
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Mesajınızı yazın..."
                        value={adminMessage}
                        onChange={(e) => setAdminMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendAdminMessage()}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <button
                      onClick={sendAdminMessage}
                      disabled={!adminMessage.trim()}
                      className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Send className="w-4 h-4" />
                      <span>Gönder</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Server Settings Tab */}
          {activeTab === 'server' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-xl overflow-hidden"
            >
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-6">Sunucu Ayarları</h3>
                
                {serverSettings ? (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Sunucu Adı
                      </label>
                      <input
                        type="text"
                        value={serverSettings.serverName || ''}
                        onChange={(e) => setServerSettings((prev: any) => ({ ...prev, serverName: e.target.value }))}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Sunucu adını girin"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Sunucu Açıklaması
                      </label>
                      <textarea
                        value={serverSettings.serverDescription || ''}
                        onChange={(e) => setServerSettings((prev: any) => ({ ...prev, serverDescription: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                        placeholder="Sunucu açıklamasını girin"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Discord Linki
                      </label>
                      <input
                        type="url"
                        value={serverSettings.discordLink || ''}
                        onChange={(e) => setServerSettings((prev: any) => ({ ...prev, discordLink: e.target.value }))}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="https://discord.gg/..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Başvuru Bekleme Mesajı
                      </label>
                      <input
                        type="text"
                        value={serverSettings.applicationStatusText || ''}
                        onChange={(e) => setServerSettings((prev: any) => ({ ...prev, applicationStatusText: e.target.value }))}
                        className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Formunuz Kontrol Aşamasındadır"
                      />
                    </div>

                    <div className="border-t border-dark-700/50 pt-6">
                      <h4 className="text-lg font-semibold text-white mb-4">Sunucu Kartları</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {serverSettings.servers?.map((server: any, index: number) => (
                          <div key={index} className="bg-dark-700/30 rounded-lg border border-dark-600 p-6 hover:bg-dark-700/50 transition-all duration-300">
                            {/* Server Header */}
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h5 className="text-xl font-semibold text-white mb-2">{server.name}</h5>
                                {server.owner && (
                                  <p className="text-primary-400 text-sm font-medium mb-1">👑 {server.owner}</p>
                                )}
                                <p className="text-gray-400 text-sm">{server.description}</p>
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    await deleteServer(server.id);
                                  } catch (error) {
                                    console.error('Sunucu silinirken hata:', error);
                                  }
                                }}
                                className="text-red-400 hover:text-red-300 transition-colors p-1"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Server Stats */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                              <div className="flex items-center space-x-2">
                                <Users className="w-4 h-4 text-primary-400" />
                                <span className="text-sm text-gray-300">
                                  {server.players || 0}/{server.maxPlayers || 100}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className={`w-3 h-3 rounded-full ${
                                  server.status === 'online' ? 'bg-green-500' : 
                                  server.status === 'offline' ? 'bg-red-500' : 'bg-yellow-500'
                                }`}></div>
                                <span className="text-sm text-gray-300 capitalize">{server.status}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4 text-primary-400" />
                                <span className="text-sm text-gray-300">{server.uptime || '100%'}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                <span className="text-sm text-gray-300">{server.rating || '5.0'}</span>
                              </div>
                            </div>

                            {/* Discord Link */}
                            {server.discordLink && (
                              <div className="mb-4">
                                <a
                                  href={server.discordLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center space-x-2 text-primary-400 hover:text-primary-300 transition-colors text-sm"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.445.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                                  </svg>
                                  <span>Discord Sunucusu</span>
                                </a>
                              </div>
                            )}

                            {/* Features */}
                            {server.features && server.features.length > 0 && (
                              <div className="mb-4">
                                <div className="flex flex-wrap gap-2">
                                  {server.features.slice(0, 3).map((feature: string, featureIndex: number) => (
                                    <span key={featureIndex} className="px-2 py-1 bg-dark-600 text-gray-300 text-xs rounded">
                                      {feature}
                                    </span>
                                  ))}
                                  {server.features.length > 3 && (
                                    <span className="px-2 py-1 bg-dark-600 text-gray-400 text-xs rounded">
                                      +{server.features.length - 3} daha
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Edit Button */}
                            <button
                              onClick={() => {
                                setEditingServer(server);
                                setIsServerEditModalOpen(true);
                              }}
                              className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm font-medium"
                            >
                              Düzenle
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      {/* Yeni Sunucu Ekleme Butonu */}
                      <div className="mt-6">
                        <button
                          onClick={async () => {
                            try {
                              const newServerData = {
                                name: 'Yeni Sunucu',
                                owner: 'Yeni Sahip',
                                category: 'roleplay',
                                description: 'Yeni sunucu açıklaması',
                                players: 0,
                                maxPlayers: 100,
                                status: 'online',
                                ip: 'Henüz Aktif Değil',
                                discordLink: 'https://discord.gg/yenisunucu',
                                features: ['QBCore Framework', 'Custom Jobs'],
                                rating: 5.0,
                                uptime: '100%',
                                location: 'Türkiye'
                              };
                              await addServer(newServerData);
                            } catch (error) {
                              console.error('Sunucu eklenirken hata:', error);
                            }
                          }}
                          className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Yeni Sunucu Ekle
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-4 pt-4 border-t border-dark-700/50">
                      <button
                        onClick={() => {
                          setServerSettings({
                            id: 'default',
                            discordLink: 'https://discord.gg/aedonv',
                            applicationStatusText: 'Formunuz Kontrol Aşamasındadır',
                            serverName: 'Aedon Community',
                            serverDescription: 'En iyi FiveM sunucu deneyimi için buradayız!'
                          });
                        }}
                        className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                      >
                        Sıfırla
                      </button>
                      <button
                        onClick={() => updateServerSettings(serverSettings)}
                        className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                      >
                        Kaydet
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                    <p className="text-gray-400">Sunucu ayarları yükleniyor...</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Application Detail Modal */}
      <AnimatePresence>
        {isApplicationDetailOpen && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-700/50">
              <h2 className="text-2xl font-bold text-white">Başvuru Detayları</h2>
              <button
                onClick={() => {
                  setIsApplicationDetailOpen(false);
                  setSelectedApplication(null);
                }}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Başvuru Bilgileri */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-dark-700/50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-300">Kullanıcı Email</label>
                  <p className="text-white">{selectedApplication.userEmail}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300">Durum</label>
                  <p className={`font-medium ${
                    selectedApplication.status === 'approved' ? 'text-green-400' :
                    selectedApplication.status === 'rejected' ? 'text-red-400' : 'text-yellow-400'
                  }`}>
                    {selectedApplication.status === 'approved' ? 'Onaylandı' :
                     selectedApplication.status === 'rejected' ? 'Reddedildi' : 'Beklemede'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300">Başvuru Tarihi</label>
                  <p className="text-white">
                    {selectedApplication.createdAt?.toDate ? 
                      selectedApplication.createdAt.toDate().toLocaleDateString('tr-TR') : 
                      'Tarih bilgisi yok'
                    }
                  </p>
                </div>
              </div>

              {/* Roleplay Soruları */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Roleplay Soruları</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Fail RP nedir?
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.failRp || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Meta Gaming Nedir?
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.metaGaming || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      RDM Ve VDM Nedir?
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.rdmVdm || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Non-RP Örnekleri veriniz.
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.nonRp || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      GOOA nedir? (Gun out of ass)
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.gooa || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      IC/OOC Nedir? Mixing Nedir?
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.icOoc || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Revenge Kill Nedir?
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.revengeKill || 'Cevap verilmemiş'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Karakter Bilgileri */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Karakter Bilgileri</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Karakterinizin Adı
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                      {selectedApplication.characterName || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Karakterinizin Hikayesi
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white min-h-[100px]">
                      {selectedApplication.characterStory || 'Cevap verilmemiş'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Karakterinizin Amacı
                    </label>
                    <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white min-h-[100px]">
                      {selectedApplication.characterGoal || 'Cevap verilmemiş'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-4 p-6 border-t border-dark-700/50">
              <button
                onClick={() => {
                  setIsApplicationDetailOpen(false);
                  setSelectedApplication(null);
                }}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Kapat
              </button>
            </div>
          </motion.div>
        </div>
        )}
      </AnimatePresence>

      {/* Server Düzenleme Modal */}
      {isServerEditModalOpen && editingServer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">Sunucu Düzenle</h3>
              <button
                onClick={() => {
                  setIsServerEditModalOpen(false);
                  setEditingServer(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await updateServer(editingServer.id, editingServer);
                  setIsServerEditModalOpen(false);
                  setEditingServer(null);
                } catch (error) {
                  console.error('Sunucu güncellenirken hata:', error);
                }
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sunucu Adı
                  </label>
                  <input
                    type="text"
                    value={editingServer.name || ''}
                    onChange={(e) => setEditingServer({...editingServer, name: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sunucu Sahibi
                  </label>
                  <input
                    type="text"
                    value={editingServer.owner || ''}
                    onChange={(e) => setEditingServer({...editingServer, owner: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Kategori
                  </label>
                  <select
                    value={editingServer.category || 'roleplay'}
                    onChange={(e) => setEditingServer({...editingServer, category: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                  >
                    <option value="roleplay">Roleplay</option>
                    <option value="racing">Yarış</option>
                    <option value="survival">Hayatta Kalma</option>
                    <option value="freeroam">Serbest Oyun</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Durum
                  </label>
                  <select
                    value={editingServer.status || 'online'}
                    onChange={(e) => setEditingServer({...editingServer, status: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                  >
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                    <option value="maintenance">Bakım</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Oyuncu Sayısı
                  </label>
                  <input
                    type="number"
                    value={editingServer.players || 0}
                    onChange={(e) => setEditingServer({...editingServer, players: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Maksimum Oyuncu
                  </label>
                  <input
                    type="number"
                    value={editingServer.maxPlayers || 100}
                    onChange={(e) => setEditingServer({...editingServer, maxPlayers: parseInt(e.target.value) || 100})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Uptime
                  </label>
                  <input
                    type="text"
                    value={editingServer.uptime || '100%'}
                    onChange={(e) => setEditingServer({...editingServer, uptime: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Rating
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={editingServer.rating || 5.0}
                    onChange={(e) => setEditingServer({...editingServer, rating: parseFloat(e.target.value) || 5.0})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    IP Adresi
                  </label>
                  <input
                    type="text"
                    value={editingServer.ip || ''}
                    onChange={(e) => setEditingServer({...editingServer, ip: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Discord Link
                  </label>
                  <input
                    type="url"
                    value={editingServer.discordLink || ''}
                    onChange={(e) => setEditingServer({...editingServer, discordLink: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Konum
                  </label>
                  <input
                    type="text"
                    value={editingServer.location || ''}
                    onChange={(e) => setEditingServer({...editingServer, location: e.target.value})}
                    className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={editingServer.description || ''}
                  onChange={(e) => setEditingServer({...editingServer, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Özellikler (virgülle ayırın)
                </label>
                <input
                  type="text"
                  value={editingServer.features ? editingServer.features.join(', ') : ''}
                  onChange={(e) => setEditingServer({...editingServer, features: e.target.value.split(',').map(f => f.trim()).filter(f => f)})}
                  className="w-full px-3 py-2 bg-dark-600 border border-dark-500 rounded text-white text-sm"
                  placeholder="QBCore Framework, Custom Jobs, Housing System"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsServerEditModalOpen(false);
                    setEditingServer(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sponsor Detail Modal */}
      <AnimatePresence>
        {isSponsorDetailOpen && selectedSponsor && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-dark-700/50">
                <h2 className="text-2xl font-bold text-white">Sponsor Detayları</h2>
                <button
                  onClick={() => {
                    setIsSponsorDetailOpen(false);
                    setSelectedSponsor(null);
                  }}
                  className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Sponsor Bilgileri */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-dark-700/50 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Kullanıcı Email</label>
                    <p className="text-white">{selectedSponsor.userEmail}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-300">Durum</label>
                    <p className={`font-medium ${
                      selectedSponsor.status === 'approved' ? 'text-green-400' :
                      selectedSponsor.status === 'rejected' ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      {selectedSponsor.status === 'approved' ? 'Onaylandı' :
                       selectedSponsor.status === 'rejected' ? 'Reddedildi' : 'Beklemede'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-300">Başvuru Tarihi</label>
                    <p className="text-white">
                      {selectedSponsor.createdAt?.toDate ? 
                        selectedSponsor.createdAt.toDate().toLocaleDateString('tr-TR') : 
                        'Tarih bilgisi yok'
                      }
                    </p>
                  </div>
                </div>

                {/* Şirket Bilgileri */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Şirket Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Şirket Adı</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.companyName}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Şirket Türü</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.companyType}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Website</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.website || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">E-posta</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.email}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Telefon</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.phone || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Adres</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.address || 'Belirtilmemiş'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* İletişim Bilgileri */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">İletişim Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">İletişim Kişisi</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.contactPerson}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Pozisyon</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.contactTitle || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">İletişim E-postası</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.contactEmail}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">İletişim Telefonu</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.contactPhone || 'Belirtilmemiş'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sponsorluk Detayları */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Sponsorluk Detayları</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Sponsorluk Türü</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.sponsorshipType}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Bütçe Aralığı</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.budget}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Süre</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.duration} Ay
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Hedef Kitle</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.targetAudience || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Beklenen Erişim</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.expectedReach || 'Belirtilmemiş'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ek Bilgiler */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Ek Bilgiler</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Önceki Sponsorluk Deneyimi</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.previousSponsorship || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Pazarlama Hedefleri</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.marketingGoals || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Özel Gereksinimler</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.specialRequirements || 'Belirtilmemiş'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Ek Bilgiler</label>
                      <div className="p-3 bg-dark-700/30 border border-dark-600 rounded-lg text-white">
                        {selectedSponsor.additionalInfo || 'Belirtilmemiş'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {selectedSponsor.status === 'pending' && (
                  <div className="flex justify-end space-x-4 pt-6 border-t border-dark-700/50">
                    <button
                      onClick={() => {
                        updateSponsorStatus(selectedSponsor.id, 'approved');
                        setIsSponsorDetailOpen(false);
                        setSelectedSponsor(null);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                    >
                      <Check className="w-4 h-4" />
                      <span>Onayla</span>
                    </button>
                    <button
                      onClick={() => {
                        updateSponsorStatus(selectedSponsor.id, 'rejected');
                        setIsSponsorDetailOpen(false);
                        setSelectedSponsor(null);
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                    >
                      <X className="w-4 h-4" />
                      <span>Reddet</span>
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Ban/Timeout Pop-up Modal */}
      <AnimatePresence>
        {showBanTimeoutPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowBanTimeoutPanel(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-dark-800/95 backdrop-blur-md border border-dark-700/50 rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white">Ban ve Timeout Yönetimi</h3>
                  <button
                    onClick={() => setShowBanTimeoutPanel(false)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex space-x-4">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Kullanıcı ara..."
                          value={banTimeoutSearchTerm}
                          onChange={(e) => setBanTimeoutSearchTerm(e.target.value)}
                          className="w-64 px-4 py-2 bg-dark-700/50 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Banlanan Kullanıcılar */}
                  <div className="mb-6">
                    <h5 className="text-md font-medium text-white mb-3 flex items-center space-x-2">
                      <Ban className="w-4 h-4 text-red-400" />
                      <span>Banlanan Kullanıcılar ({bannedUsers.length})</span>
                    </h5>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {bannedUsers
                        .filter(user => {
                          const matchesSearch = user.displayName?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase()) ||
                                              user.email?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase()) ||
                                              user.banReason?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase());
                          return matchesSearch;
                        })
                        .map((user: any) => (
                        <div key={user.id} className="bg-dark-700/50 rounded-lg p-4 border border-dark-600">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                                <Ban className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h4 className="text-white font-medium">{user.displayName}</h4>
                                <p className="text-sm text-gray-400">{user.email}</p>
                                <p className="text-xs text-red-400">
                                  Ban Sebebi: {user.banReason || 'Belirtilmemiş'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Ban Tarihi: {user.bannedAt ? new Date(user.bannedAt.seconds * 1000).toLocaleString('tr-TR') : 'Bilinmeyen'}
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => unbanUser(user.id)}
                                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                                title="Banı Aç"
                              >
                                <UserCheck className="w-4 h-4" />
                                <span>Banı Aç</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {bannedUsers.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <Ban className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Banlanan kullanıcı bulunmuyor</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Timeout Edilen Kullanıcılar */}
                  <div>
                    <h5 className="text-md font-medium text-white mb-3 flex items-center space-x-2">
                      <Timer className="w-4 h-4 text-yellow-400" />
                      <span>Timeout Edilen Kullanıcılar ({timeoutUsers.length})</span>
                    </h5>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {timeoutUsers
                        .filter(user => {
                          const matchesSearch = user.displayName?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase()) ||
                                              user.email?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase()) ||
                                              user.reason?.toLowerCase().includes(banTimeoutSearchTerm.toLowerCase());
                          return matchesSearch;
                        })
                        .map((user: any) => (
                        <div key={user.id} className="bg-dark-700/50 rounded-lg p-4 border border-dark-600">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                                <Timer className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h4 className="text-white font-medium">{user.displayName}</h4>
                                <p className="text-sm text-gray-400">{user.email || 'Email bulunamadı'}</p>
                                <p className="text-xs text-yellow-400">
                                  Timeout Sebebi: {user.reason || 'Belirtilmemiş'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Kalan Süre: {user.remainingTime} dakika
                                </p>
                                <p className="text-xs text-gray-500">
                                  Timeout Tarihi: {user.timeoutAt ? new Date(user.timeoutAt).toLocaleString('tr-TR') : 'Bilinmeyen'}
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => removeTimeout(user.id)}
                                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                                title="Timeout'u Kaldır"
                              >
                                <UserCheck className="w-4 h-4" />
                                <span>Timeout Kaldır</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {timeoutUsers.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <Timer className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Timeout edilen kullanıcı bulunmuyor</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Product Edit Modal */}
        <AnimatePresence>
          {isProductEditModalOpen && editingProduct && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={() => setIsProductEditModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-dark-800 border border-dark-700 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-white">
                    {editingProduct.id ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
                  </h3>
                  <button
                    onClick={() => setIsProductEditModalOpen(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Ürün Adı</label>
                      <input
                        type="text"
                        value={editingProduct.name}
                        onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Ürün adını girin"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Kategori</label>
                      <select
                        value={editingProduct.category}
                        onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="vehicles">Araçlar</option>
                        <option value="clothing">Kıyafetler</option>
                        <option value="weapons">Silahlar</option>
                        <option value="properties">Mülkler</option>
                        <option value="accessories">Aksesuarlar</option>
                        <option value="games">Oyunlar</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Açıklama</label>
                    <textarea
                      value={editingProduct.description}
                      onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={3}
                      placeholder="Ürün açıklamasını girin"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Fiyat</label>
                      <input
                        type="number"
                        value={editingProduct.price}
                        onChange={(e) => setEditingProduct({...editingProduct, price: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Para Birimi</label>
                      <select
                        value={editingProduct.currency}
                        onChange={(e) => setEditingProduct({...editingProduct, currency: e.target.value})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="TL">TL</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Versiyon</label>
                      <input
                        type="text"
                        value={editingProduct.version}
                        onChange={(e) => setEditingProduct({...editingProduct, version: e.target.value})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="1.0.0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Resim URL</label>
                    <input
                      type="url"
                      value={editingProduct.image}
                      onChange={(e) => setEditingProduct({...editingProduct, image: e.target.value})}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Özellikler (Her satıra bir özellik)</label>
                    <textarea
                      value={editingProduct.features.join('\n')}
                      onChange={(e) => setEditingProduct({...editingProduct, features: e.target.value.split('\n').filter(f => f.trim())})}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={4}
                      placeholder="Özellik 1&#10;Özellik 2&#10;Özellik 3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Sunucu</label>
                    <input
                      type="text"
                      value={editingProduct.server}
                      onChange={(e) => setEditingProduct({...editingProduct, server: e.target.value})}
                      className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Sunucu adını girin (örn: Aedon RP #1)"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Geliştirici</label>
                      <input
                        type="text"
                        value={editingProduct.author}
                        onChange={(e) => setEditingProduct({...editingProduct, author: e.target.value})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Geliştirici adı"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Puan</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        step="0.1"
                        value={editingProduct.rating}
                        onChange={(e) => setEditingProduct({...editingProduct, rating: parseFloat(e.target.value) || 5.0})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Oyuncu Sayısı</label>
                      <input
                        type="number"
                        min="0"
                        value={editingProduct.players}
                        onChange={(e) => setEditingProduct({...editingProduct, players: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-dark-700/50">
                  <button
                    onClick={() => setIsProductEditModalOpen(false)}
                    className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        if (editingProduct.id) {
                          await updateMarketProduct(editingProduct.id, editingProduct);
                        } else {
                          await addMarketProduct(editingProduct);
                        }
                        setIsProductEditModalOpen(false);
                        setEditingProduct(null);
                      } catch (error) {
                        console.error('Ürün kaydedilirken hata:', error);
                      }
                    }}
                    className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>{editingProduct.id ? 'Güncelle' : 'Ekle'}</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </AnimatePresence>
    </>
  );
};

export default AdminPanel;
